import {
  composeMessageObject,
  composeGreetingMessageObject,
  composeFeedbackOptionsMessageObject,
  composeFeedbackClosingMessageObject,
  composeTransactionMessageObject,
  composeTrxNotFoundMessageObject,
  composeContextFromInitialSelectedTrx,
  SYSTEM_COMMANDS,
  FEEDBACK_MESSAGES,
  LS_KEYS,
} from '~/utils/mitra-chat-gpt';
import { base64UrlToJson } from '~/utils/base64-utils';
import mitraChatGptEventBus, { BUS_EVENTS } from '~/components/MitraChatGpt/mitraChatGptEventBus';
import {
  createSession,
  sendMessageOrAttachment,
  getCurrentSession,
  updateCurrentSessionStatus,
} from '~/api/mitra-chat-gpt/exclusive/helps-chats';
import { getMitraPaymentTransactions } from '~/api/mitra-payment';
import { ACTION_TYPES } from './mitra-chat-gpt.types';

export const SELECTED_TRX_SCHEMA = {
  sessionId: '',
  data: null,
};

export const GREETING_MESSAGE = composeGreetingMessageObject();

export const DEFAULT_ASYNC_STATE = {
  loading: false,
  data: null,
  error: null,
};

export const INITIAL_STATE = {
  session: {
    session_id: null,
    expire_at: null,
    user_id: null,
    state: null,
    updated_at: null,
    latest_crt: null,
    zendesk_ticket_id: null,
  },
  etag: null, // this will be used for polling on getting current session/chat log
  shouldGreetUser: false, // decides whether to display greeting message
  messages: [],
  messageOptions: {
    type: '',
    data: [],
  },
  selectedTransaction: null, // this would persist the selected transaction data, prevents us from losing context in the current session

  // Async states
  createSession: { ...DEFAULT_ASYNC_STATE },
  sendMessage: { ...DEFAULT_ASYNC_STATE },
  getCurrentSession: { ...DEFAULT_ASYNC_STATE },
  closeCurrentSession: { ...DEFAULT_ASYNC_STATE },
  transactions: { ...DEFAULT_ASYNC_STATE }, // this would be used for <TransactionOverlay /> component
};

export const state = () => ({ ...INITIAL_STATE });

export const getters = {
  /**
   * @param {INITIAL_STATE} state
   * @returns {object}
   */
  getSessionData(state) {
    return state.session;
  },

  /**
   * @param {INITIAL_STATE} state
   * @returns {string}
   */
  getEtag(state) {
    return state.etag;
  },

  /**
   * @param {INITIAL_STATE} state
   * @returns {object[]}
   */
  getMessages(state) {
    return state.messages;
  },

  /**
   * @param {INITIAL_STATE} state
   * @returns {object | undefined}
   */
  getLastMessage(state) {
    const messagesFromState = [...state.messages];
    return messagesFromState.pop();
  },

  /**
   * @param {INITIAL_STATE} state
   * @returns {object}
   */
  getMessageOptions(state) {
    return state.messageOptions;
  },

  /**
   * @param {INITIAL_STATE} state
   * @returns {object}
   */
  getSelectedTransaction(state) {
    if (process.browser) {
      const itemFromLS = localStorage.getItem(LS_KEYS.gptSelectedTransaction);
      return state.selectedTransaction || JSON.parse(itemFromLS);
    }

    return state.selectedTransaction;
  },
};

export const mutations = {
  /**
   * Populate the session data
   * @param {INITIAL_STATE} state
   * @param {object} payload
   */
  setSessionData(state, payload) {
    state.session = {
      ...INITIAL_STATE.session,
      session_id: payload.session_id,
      expire_at: payload.expire_at,
      user_id: payload.user_id,
      state: payload.state,
      updated_at: payload.updated_at,
      latest_crt: payload.latest_crt,
      zendesk_ticket_id: payload.zendeks_ticket,
    };
  },

  /**
   * @param {INITIAL_STATE} state
   * @param {string} payload
   */
  setEtag(state, payload) {
    state.etag = payload;
  },

  /**
   * @param {INITIAL_STATE} state
   * @param {boolean} payload
   */
  setShouldGreetUser(state, payload) {
    state.shouldGreetUser = payload;
  },

  /**
   * Used to pre-populate the `messages` array, typically used in `createSession` or `getSession` action
   * @param {INITIAL_STATE} state
   * @param {object[]} payload
   */
  setMessages(state, payload) {
    const reversedMessages = [...payload].reverse();

    // Conditionally populate the `messages` with the GREETING_MESSAGE and the messages from the API
    if (state.shouldGreetUser) {
      state.messages = [GREETING_MESSAGE, ...reversedMessages];
      return;
    }
    state.messages = [...reversedMessages];
  },

  /**
   * Add a new singular message object into the `messages` array, typically used when the user sends a new message
   * @param {INITIAL_STATE} state
   * @param {object} payload
   */
  addNewMessage(state, payload) {
    state.messages = [...state.messages, payload];
  },

  /**
   * @param {INITIAL_STATE} state
   * @param {object} payload
   * @param {string} payload.type
   * @param {object[]} payload.data
   */
  setMessageOptions(state, { type = '', data = [] }) {
    state.messageOptions = {
      type,
      data,
    };
  },

  /**
   * @param {INITIAL_STATE} state
   * @param {object} payload
   * @param {string} payload.sessionId
   * @param {object | null} payload.data
   */
  setSelectedTransaction(state, { sessionId = '', data = null }) {
    state.selectedTransaction = { sessionId, data };
    localStorage.setItem(
      LS_KEYS.gptSelectedTransaction,
      JSON.stringify({
        ...SELECTED_TRX_SCHEMA,
        sessionId,
        data,
      })
    );
  },

  /**
   * @param {INITIAL_STATE} state
   */
  removeSelectedTransaction(state) {
    state.selectedTransaction = INITIAL_STATE.selectedTransaction;
    localStorage.removeItem(LS_KEYS.gptSelectedTransaction);
  },

  /**
   * @param {INITIAL_STATE} state
   */
  removeMessageOptions(state) {
    state.messageOptions = INITIAL_STATE.messageOptions;
  },

  /**
   * @param {INITIAL_STATE} state
   */
  beginCreateSession(state) {
    state.createSession = {
      ...INITIAL_STATE.createSession,
      loading: true,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   * @param {object} payload
   * @param {object} payload.data - returned session data from API
   */
  successCreateSession(state, { data }) {
    state.createSession = {
      ...INITIAL_STATE.createSession,
      data,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   */
  errorCreateSession(state, { error }) {
    state.createSession = {
      ...INITIAL_STATE.createSession,
      error,
    };
  },

  /**
   * @param {INITIAL_STATE} state
   */
  beginSendMessage(state) {
    state.sendMessage = {
      ...INITIAL_STATE.sendMessage,
      loading: true,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   * @param {object} payload
   * @param {obbject} payload.data - returned data from the POST message API
   */
  successSendMessage(state, { data }) {
    state.sendMessage = {
      ...INITIAL_STATE.sendMessage,
      data,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   */
  errorSendMessage(state, { error }) {
    state.sendMessage = {
      ...INITIAL_STATE.sendMessage,
      error,
    };
  },

  /**
   * @param {INITIAL_STATE} state
   */
  beginGetCurrentSession(state) {
    state.getCurrentSession = {
      ...INITIAL_STATE.getCurrentSession,
      loading: true,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   * @param {object} payload
   * @param {object} payload.data - returned session data from API
   */
  successGetCurrentSession(state, { data }) {
    state.getCurrentSession = {
      ...INITIAL_STATE.getCurrentSession,
      data,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   */
  errorGetCurrentSession(state, { error }) {
    state.getCurrentSession = {
      ...INITIAL_STATE.getCurrentSession,
      error,
    };
  },

  /**
   * @param {INITIAL_STATE} state
   */
  beginCloseCurrentSessionStatus(state) {
    state.closeCurrentSession = {
      ...INITIAL_STATE.closeCurrentSession,
      loading: true,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   * @param {object} payload
   * @param {object} payload.data - returned session data from API
   */
  successCloseCurrentSessionStatus(state, { data }) {
    state.closeCurrentSession = {
      ...INITIAL_STATE.closeCurrentSession,
      data,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   */
  errorCloseCurrentSessionStatus(state, { error }) {
    state.closeCurrentSession = {
      ...INITIAL_STATE.closeCurrentSession,
      error,
    };
  },

  /**
   * @param {INITIAL_STATE} state
   */
  beginGetTransactions(state) {
    state.transactions = {
      ...INITIAL_STATE.transactions,
      loading: true,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   * @param {object} payload
   * @param {object[]} payload.data - returned transaction data from API
   */
  successGetTransactions(state, { data }) {
    state.transactions = {
      ...INITIAL_STATE.transactions,
      data,
    };
  },
  /**
   * @param {INITIAL_STATE} state
   */
  errorGetTransactions(state, { error }) {
    state.transactions = {
      ...INITIAL_STATE.transactions,
      error,
    };
  },
};

export const actions = {
  /**
   * @param {object} context
   * @param {object} payload
   * @param {string} payload.sessionId
   * @param {object | null} payload.data
   */
  setSelectedTransaction({ commit, getters }, { sessionId = '', data = null }) {
    const selectedTransaction = getters.getSelectedTransaction;

    // Will store selectedTransaction if there is no `selectedTransaction` in localStorage
    if (!selectedTransaction && data) {
      commit('setSelectedTransaction', { sessionId, data });
      return;
    }

    // Will store selectedTransaction into localStorage if the one in the localStorage has different `sessionId` with the incoming data
    if (selectedTransaction?.sessionId !== sessionId && data) {
      commit('setSelectedTransaction', { sessionId, data });
    }
  },

  /**
   * This will send one of these template messages at the beginning of the session
   * Greeting Message
   * Transaction Template Message
   * @param {object} context
   * @param {object} payload
   * @param {string} payload.sessionId - selected transaction object in a base64 format
   * @param {string | null} payload.selectedTransaction - selected transaction object in a base64 format
   * @returns {Promise<object>}
   */
  async handleSessionOpening({ commit, dispatch }, { sessionId = '', selectedTransaction = '' }) {
    try {
      // if the user has selected a transaction, send a transaction template message
      if (selectedTransaction) {
        const trxMessage = composeTransactionMessageObject(selectedTransaction);
        await dispatch(ACTION_TYPES.sendMessage, {
          content: trxMessage.content,
          context: composeContextFromInitialSelectedTrx(selectedTransaction),
        });
        dispatch(ACTION_TYPES.setSelectedTransaction, { sessionId, data: base64UrlToJson(selectedTransaction) });
        return trxMessage;
      }

      // Try to set `shouldGreetUser` to true if there are no messages in the current session and no selected transaction
      commit('setShouldGreetUser', true);
      commit('removeSelectedTransaction');
      return null;
    } catch (error) {
      console.error('[ERROR] Something went wrong in the handleSessionOpening()', error);
      throw error;
    }
  },

  /**
   * Start session by creating a new session or getting the current session
   * @param {object} context
   * @param {object} payload
   * @param {string | null} payload.selectedTransaction - selected transaction object in a base64 format
   */
  async createSession({ commit, dispatch }, { selectedTransaction = '' }) {
    try {
      commit('beginCreateSession');
      const { data: sessionData } = await createSession(this.$axios);
      commit('successCreateSession', { data: sessionData });

      // Populate the session data
      commit('setSessionData', sessionData);

      // Save the session id to local storage
      // This actions is needed to help retrieve the current chat gpt session in inbox
      window.localStorage.setItem('giselle_chat_session_id', sessionData.session_id);

      /**
       * @type {object[]}
       */
      let sessionChats = sessionData.chats;

      // handle current chat with no messages
      if (sessionChats.length === 0) {
        const openingMessage = await dispatch(ACTION_TYPES.handleSessionOpening, {
          sessionId: sessionData.session_id,
          selectedTransaction,
        });
        sessionChats = openingMessage ? [openingMessage] : [];
      }

      // Try to populate the `messages` state with the `chats` from the session data
      commit('setMessages', sessionChats);

      // latest message is placed on the very top of the `chats` array in the API response
      const lastMessage = sessionChats[0];
      // Try to populate message options with the `option` from the lastMessage
      if (lastMessage?.option) {
        commit('setMessageOptions', {
          type: lastMessage.option.type,
          data: lastMessage.option.data,
        });
      }

      // Try to display feedback quick chat if the last message tells us to ask for feedback
      if (lastMessage?.system_command === SYSTEM_COMMANDS.ask_for_feedback) {
        dispatch(ACTION_TYPES.askForFeedback);
      }

      // Will show gpt typing info if the last message in the session is a message from the User themself
      const isLastMessageFromUser = lastMessage && !lastMessage.ref_chat_id;
      if (isLastMessageFromUser) {
        mitraChatGptEventBus.$emit(BUS_EVENTS.showGptTypingInfo);
      }
    } catch (error) {
      commit('errorCreateSession', { error });
      throw error;
    }
  },

  /**
   * Send any text message to the current session
   * @param {object} context
   * @param {object} payload
   * @param {string} payload.content
   * @param {string} payload.attachment
   * @param {object | null} payload.context
   */
  async sendMessage({ commit, state }, { content = '', attachment = '', context = null }) {
    try {
      // remove message options
      commit('removeMessageOptions');

      // Optimistically add new message object into the `messages` state
      commit('addNewMessage', composeMessageObject({ content, attachment }));

      // Actually send message with API
      commit('beginSendMessage');
      const { session_id: sessionId } = state.session;
      const strippedBase64Attachment = attachment ? attachment.split(',')[1] : '';
      const { data: sendMessageData } = await sendMessageOrAttachment(this.$axios, {
        sessionId,
        content,
        attachment: strippedBase64Attachment,
        context,
      });
      commit('successSendMessage', { data: sendMessageData });

      // will show gpt typing info if successfully sent message
      mitraChatGptEventBus.$emit(BUS_EVENTS.showGptTypingInfo);
    } catch (error) {
      commit('errorSendMessage', { error });
      throw error;
    }
  },

  /**
   * This action would be called with a polling after `createSession` or `sendMessage` action
   * @param {object} context
   */
  async getCurrentSession({ commit, dispatch, state }) {
    try {
      commit('beginGetCurrentSession');

      // Retrieve the sessionId and etag from the getters
      const { session_id: sessionId } = state.session;
      const { etag } = state;

      // Try to get current session from the API
      const {
        data: { data: sessionData },
        headers,
      } = await getCurrentSession(this.$axios, {
        sessionId,
        etag,
      });
      commit('successGetCurrentSession', { data: sessionData });

      // Populate the session data
      commit('setSessionData', sessionData);
      // Set the value of etag again
      commit('setEtag', headers.etag);
      // Try to populate the `messages` state with the `chats` from the session data
      commit('setMessages', sessionData.chats);

      // latest message is placed on the very top of the `chats` array in the API response
      const lastMessage = sessionData.chats[0];
      // Try to populate message options with the `option` from the lastMessage
      if (lastMessage?.option) {
        commit('setMessageOptions', {
          type: lastMessage.option.type,
          data: lastMessage.option.data,
        });
      } else {
        commit('removeMessageOptions');
      }

      // Try to display feedback quick chat if the last message tells us to ask for feedback
      if (lastMessage?.system_command === SYSTEM_COMMANDS.ask_for_feedback) {
        dispatch(ACTION_TYPES.askForFeedback);
      }

      // Will show gpt typing info if the last message in the session is a message from the User themself
      const isLastMessageFromUser = lastMessage && !lastMessage.ref_chat_id;
      if (isLastMessageFromUser) {
        mitraChatGptEventBus.$emit(BUS_EVENTS.showGptTypingInfo);
      } else {
        mitraChatGptEventBus.$emit(BUS_EVENTS.hideGptTypingInfo);
      }
    } catch (error) {
      const hasNoNewReplyFromGpt = error.message.includes('304');
      if (hasNoNewReplyFromGpt) {
        return;
      }

      commit('errorGetCurrentSession', { error });
      throw error;
    }
  },

  /**
   * Display feedback quick chat option
   * @param {object} context
   */
  askForFeedback({ commit }) {
    const message = composeFeedbackOptionsMessageObject();
    commit('addNewMessage', message);
    commit('setMessageOptions', {
      type: message.option.type,
      data: message.option.data,
    });
  },

  /**
   * Append feedback message to the message list (Vuex state)
   * This would not send the message into the current session
   * @param {object} context
   * @param {object} payload
   * @param {string} payload.content
   */
  sendFeedbackMessage({ commit }, { content = '' }) {
    // remove message options
    commit('removeMessageOptions');

    // add the selected feedback message into the message list
    commit('addNewMessage', composeMessageObject({ content }));

    // add the closing messages based on the selected feedback sentiment
    const hasPositiveSentiment = content === FEEDBACK_MESSAGES.positive;
    const closingMessages = [...composeFeedbackClosingMessageObject(hasPositiveSentiment)];
    closingMessages.forEach(message => {
      commit('addNewMessage', message);
    });
  },

  /**
   * Close the current session
   * @param {object} context
   */
  async closeCurrentSession({ commit, state }) {
    try {
      commit('beginCloseCurrentSessionStatus');

      const { session_id: gptSessionId } = state.session;
      const bodyReq = {
        sessionId: gptSessionId || window.localStorage.getItem('giselle_chat_session_id'),
        state: 'closed',
      };
      const { message } = await updateCurrentSessionStatus(this.$axios, bodyReq);
      commit('successCloseCurrentSessionStatus', { data: message });
      return message;
    } catch (error) {
      commit('errorCloseCurrentSessionStatus', { error });
      throw error;
    }
  },

  /**
   * Gets the transactions for the <TransactionOverlay /> component
   * @param {object} context
   */
  async getTransactions({ commit }) {
    try {
      commit('beginGetTransactions');

      const { data: invoicesTrx } = await getMitraPaymentTransactions(this.$axios, { trxType: 'invoices' });
      const { data: buka20Trx } = await getMitraPaymentTransactions(this.$axios, { trxType: 'buka20-transactions' });
      const transactions = [...invoicesTrx, ...buka20Trx];

      const sorted = [...transactions];
      sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      commit('successGetTransactions', { data: [...sorted] });
      return sorted;
    } catch (error) {
      commit('errorGetTransactions', { error });
      throw error;
    }
  },

  /**
   * When the user clicks "Transaksi tidak ditemukan", it would automatically send closing messages
   * @param {object} context
   */
  sendTrxNotFoundClosingMessages({ commit }) {
    const [userMessage, gptMessage] = composeTrxNotFoundMessageObject();
    commit('addNewMessage', userMessage);
    commit('addNewMessage', gptMessage);
  },

  /**
   * Get the current of chat gpt session that has been created before from inbox
   * Check from state, it will return 'active' if there is an active one
   * Check from chats, it make sure that the user has talked to the GPT
   */
  async hasActiveChatGptSession() {
    try {
      const sessionId = window.localStorage.getItem('giselle_chat_session_id');
      if (!sessionId) return false;

      const { data: { data } } = await getCurrentSession(this.$axios, { sessionId });
      return data.state === 'active' && data.chats.length > 0;
    } catch (error) {
      return false;
    }
  },
};
